// ============================================================================
// i18n.js — a kezelőfelület nyelvkezelése.
//
// A SZÖVEGEK NEM ITT VANNAK, hanem a projekt gyökerében lévő `nyelvek.json`
// fájlban - ez a nyelvek EGYETLEN forrása (felület, csevegés, értesítés).
// A build ebből generálja a `ui-strings.js` modult (scripts/nyelvek.mjs):
//
//     "chat.send": { "en": "Send", "hu": "Küldés", "ja": "送信", ... }
//
// Egy új nyelv felvétele így csak adatmódosítás + a szokásos kiadás.
//
// FONTOS MEGKÜLÖNBÖZTETÉS (SPECIFICATION.md 1. szakasz):
//   - "Kezelőfelület nyelve" (uiLang, ez a modul): MINDEN feliratot,
//     gombot, hibaüzenetet vezérel. Bejelentkezés ELŐTT is választható.
//   - "Tartalmi/csevegési nyelv": a nyelvek.json "cseveges": true nyelvei
//     (CHAT_LANGUAGES) - a szerver ugyanebből a listából ellenőriz.
// ============================================================================

import uiStrings from "./ui-strings.js";

/** Az alapértelmezett (tartalék) nyelv: ezt kapja minden új látogató. */
export const DEFAULT_UI_LANG = uiStrings.defaultLanguage || "en";

/** A választható felületi nyelvek kódjai, a JSON sorrendjében. */
export const SUPPORTED_UI_LANGS = Object.keys(uiStrings.languages || {});

/**
 * A nyelvek saját írásmódú neve (日本語, Magyar, English). Ezeket
 * szándékosan NEM fordítjuk: a céljuk épp az, hogy bárki felismerje a
 * sajátját, bármilyen nyelvű felületen áll is éppen.
 */
export const UI_LANG_NATIVE_NAMES = uiStrings.languages || {};

const STRINGS = uiStrings.strings || {};

// ---------------------------------------------------------------------------
// LUSTA NYELVBETÖLTÉS (2026-10-20, v126; Tamás: "folytasd a lusta betöltéssel"). A böngészős csomagban (esbuild, client/build.mjs) a feliratoknak CSAK az
// alapértelmezett nyelve (angol) van benne (`uiStrings.lazy === true`), a többi nyelv külön fájlban él (public/lang/<kód>.json, a build írja), és
// akkor töltődik le, amikor az a nyelv kell: ez a csomag méretének kb. a negyedét (tömörítve kb. 190 KB-ot) spórolja. A Node-os tesztek a TELJES
// ui-strings.js-t kapják (lazy hamis), ott a betöltés no-op, és minden nyelv azonnal elérhető.
//
// Működés: a t() egy még be nem töltött nyelvnél az angol szöveget adja, ÉS elindítja a betöltést; a megérkezéskor az `onNyelvBetoltve` figyelők
// (a main.js újrarajzol) értesülnek. Az induláskor a főprogram előbb megvárja a mentett nyelv betöltését (ensureUiLang), így nincs angol villanás.
// Ha a betöltés elbukik (offline, hálózati hiba), az angol marad, és legközelebb újra próbálkozik.
// ---------------------------------------------------------------------------
const LAZY = uiStrings.lazy === true;
const betoltott = new Set([DEFAULT_UI_LANG]);
const folyamatban = new Map(); // kód -> Promise
const figyelok = new Set();

function nyelvUrl(lang) {
  const verzio = typeof __BUILD_VERSION__ !== "undefined" ? __BUILD_VERSION__ : "";
  const alap = typeof document !== "undefined" && document.baseURI ? document.baseURI : "http://localhost/";
  return new URL(`lang/${lang}.json${verzio ? `?v=${encodeURIComponent(verzio)}` : ""}`, alap).toString();
}

/** Egy nyelv szövegeinek beillesztése a feliratok közé (kulcs -> szöveg). */
export function nyelvBeillesztese(lang, szovegek) {
  if (!szovegek || typeof szovegek !== "object") return 0;
  let db = 0;
  for (const [kulcs, szoveg] of Object.entries(szovegek)) {
    if (typeof szoveg !== "string" || szoveg.length === 0) continue;
    if (!STRINGS[kulcs]) STRINGS[kulcs] = {};
    STRINGS[kulcs][lang] = szoveg;
    db++;
  }
  betoltott.add(lang);
  return db;
}

/**
 * Biztosítja, hogy a nyelv szövegei megvannak. Teljes (nem lusta) módban, az alapnyelvnél és már betöltött nyelvnél azonnal kész.
 * Sosem dob: hiba esetén a ígéret `false`-szal teljesül (az angol tartalék marad).
 * @param {string} lang
 * @param {(url: string) => Promise<any>} [betolto] tesztekhez: a letöltés cseréje (alapból fetch + json)
 * @returns {Promise<boolean>} igaz, ha a nyelv elérhető
 */
export function ensureUiLang(lang, betolto) {
  if (!LAZY && !betolto) return Promise.resolve(true);
  if (typeof lang !== "string" || !SUPPORTED_UI_LANGS.includes(lang) || betoltott.has(lang)) return Promise.resolve(betoltott.has(lang) || lang === DEFAULT_UI_LANG);
  if (folyamatban.has(lang)) return folyamatban.get(lang);
  const letolt = betolto || ((url) => fetch(url, { credentials: "same-origin" }).then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); }));
  const igeret = Promise.resolve()
    .then(() => letolt(nyelvUrl(lang)))
    .then((szovegek) => {
      nyelvBeillesztese(lang, szovegek);
      for (const f of [...figyelok]) { try { f(lang); } catch (e) { /* a figyelő hibája nem akaszthatja meg a többit */ } }
      return true;
    })
    .catch(() => false)
    .finally(() => { folyamatban.delete(lang); });
  folyamatban.set(lang, igeret);
  return igeret;
}

/** Értesítés, ha egy nyelv szövegei megérkeztek (újrarajzoláshoz). Visszatér a leiratkozóval. */
export function onNyelvBetoltve(figyelo) {
  figyelok.add(figyelo);
  return () => figyelok.delete(figyelo);
}

export function nyelvBetoltve(lang) {
  return !LAZY || betoltott.has(lang);
}

/**
 * Egy felirat lekérése a kért nyelven, {token} helyettesítéssel.
 *
 * Tartalék-lánc: kért nyelv → alapértelmezett nyelv → maga az azonosító.
 * Az azonosító visszaadása szándékos: egy hiányzó fordítás így LÁTHATÓ
 * lesz a felületen, nem néma üres felirat, ami nehezen észrevehető hiba.
 */
export function t(uiLang, key, params) {
  const entry = STRINGS[key];
  let text;

  if (entry) {
    text = entry[uiLang];
    if (typeof text !== "string" || text.length === 0) {
      text = entry[DEFAULT_UI_LANG];
      // Lusta módban: a még be nem töltött nyelv betöltése elindul (a megérkezéskor az újrarajzolás a helyes szöveget adja).
      if (LAZY && uiLang !== DEFAULT_UI_LANG && !betoltott.has(uiLang)) ensureUiLang(uiLang);
    }
  }
  if (typeof text !== "string" || text.length === 0) {
    text = key;
  }

  if (params) {
    for (const token of Object.keys(params)) {
      text = text.replace(`{${token}}`, params[token]);
    }
  }
  return text;
}

export function isSupportedUiLang(lang) {
  return SUPPORTED_UI_LANGS.includes(lang);
}

/**
 * Felületi nyelvkód → dátum/idő formázás területi beállítása (Intl), a
 * nyelvek.json "locale" mezőjéből. (A kínai két írásváltozata régióban is
 * eltér: az egyszerűsített zh-CN, a hagyományos zh-TW formát kap.)
 */
const LOCALES = uiStrings.locales || {};

/** A választható csevegési nyelvek: {kód: saját néven}. */
export const CHAT_LANGUAGES = uiStrings.chatLanguages || {};

export function localeFor(uiLang) {
  return LOCALES[uiLang] || LOCALES[DEFAULT_UI_LANG] || "en-US";
}

/**
 * A böngésző nyelvi beállításából a legközelebbi támogatott felületi nyelv
 * (vagy null). Csak ELŐVÁLASZTÁS a nyelvválasztó képernyőn - a látogató
 * így a saját nyelvén látja a magyarázatot, de a döntés az övé marad.
 *
 *   zh-CN / zh-SG / zh-Hans-*  → zh-Hans
 *   zh-TW / zh-HK / zh-MO / zh-Hant-* → zh-Hant
 *   fil / tl → tl
 */
export function detectUiLang(browserLangs) {
  for (const raw of browserLangs || []) {
    if (typeof raw !== "string" || !raw) continue;
    const tag = raw.toLowerCase();
    // Pontos egyezés bármely felvett kóddal (pl. "pt-BR", ha van ilyen nyelv).
    const pontos = SUPPORTED_UI_LANGS.find((k) => k.toLowerCase() === tag);
    if (pontos) return pontos;
    const [base] = tag.split("-");
    let code = null;
    if (base === "zh") {
      code = /-(hant|tw|hk|mo)(-|$)/.test(tag) ? "zh-Hant" : "zh-Hans";
    } else if (base === "fil" || base === "tl") {
      code = "tl";
    } else {
      code = base;
    }
    if (isSupportedUiLang(code)) return code;
  }
  return null;
}

/** Fejlesztői segédlet: mely azonosítóknál hiányzik fordítás. */
export function findMissingTranslations() {
  const missing = [];
  for (const [key, entry] of Object.entries(STRINGS)) {
    for (const lang of SUPPORTED_UI_LANGS) {
      if (typeof entry[lang] !== "string" || entry[lang].length === 0) {
        missing.push({ key, lang });
      }
    }
  }
  return missing;
}
